import React, { useState } from "react";
import { Alert, Box, Chip, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FeatureGate } from "../../components/FeatureGate/FeatureGate";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { useAnalyticsCatalogue } from "../../api/analytics";
import { Vue } from "./tabs/Vue";
import { Explorateur } from "./tabs/Explorateur";
import { Anomalies } from "./tabs/Anomalies";
import { Couts } from "./tabs/Couts";

/**
 * Analyses avancees.
 *
 * The period selector and the freshness banner live in the shell rather than in
 * each tab: every figure below them is a read of the analytics fact table, and
 * a number whose age is invisible is a number the user over-trusts the moment
 * the refresh worker falls behind.
 */
export const Analyses: React.FC = () => {
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();

  const startOfYear = `${new Date().getFullYear()}-01-01`;
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-01`;

  const [from, setFrom] = useState(startOfYear);
  const [to, setTo] = useState(currentMonth);
  const [tab, setTab] = useState(0);

  const { catalogue } = useAnalyticsCatalogue(fleetId);
  const freshness = catalogue?.freshness;

  return (
    // Sidebar OUTSIDE FeatureGate on purpose: when the plan does not include
    // the feature, the gate blurs its children and swallows clicks. Wrapping
    // the nav in it too would leave the user with no way out of the page.
    // Sidebar's content area is `overflow: hidden`, so the scroll container is
    // ours.
    <Sidebar>
      <Box sx={{ height: "100%", overflowY: "auto" }}>
        <FeatureGate feature="advanced_analytics">
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t("analytics.title", "Analyses avancees")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("analytics.subtitle", "TCO, cout au kilometre, energie et anomalies sur l'ensemble du parc.")}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small" type="month" label={t("analytics.from", "Du")}
              value={from.slice(0, 7)}
              onChange={(e) => setFrom(`${e.target.value}-01`)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small" type="month" label={t("analytics.to", "Au")}
              value={to.slice(0, 7)}
              onChange={(e) => setTo(`${e.target.value}-01`)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Stack>

        {/* Freshness is shown, never assumed. `pending_partitions` is the honest
            signal: cells the ledger has invalidated but the worker has not
            rebuilt yet. */}
        {freshness && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Chip
              size="small"
              color={freshness.is_stale ? "warning" : "success"}
              label={
                freshness.last_computed_at
                  ? `${t("analytics.computedAt", "Donnees calculees le")} ${new Date(freshness.last_computed_at).toLocaleString("fr-FR")}`
                  : t("analytics.neverComputed", "Jamais calculees")
              }
            />
            {freshness.pending_partitions > 0 && (
              <Chip
                size="small" variant="outlined"
                label={t("analytics.pending", {
                  defaultValue: "{{count}} cellule(s) en attente de recalcul",
                  count: freshness.pending_partitions,
                })}
              />
            )}
          </Stack>
        )}

        {freshness && !freshness.last_computed_at && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t(
              "analytics.noFacts",
              "Aucune donnee analytique pour l'instant. Saisissez ou importez des couts dans l'onglet Couts : les agregats se construisent automatiquement ensuite."
            )}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t("analytics.tabs.overview", "Vue d'ensemble")} />
          <Tab label={t("analytics.tabs.explorer", "Explorateur")} />
          <Tab label={t("analytics.tabs.anomalies", "Anomalies")} />
          <Tab label={t("analytics.tabs.costs", "Couts")} />
        </Tabs>

        <Box sx={{ mt: 3 }}>
          {tab === 0 && <Vue fleetId={fleetId} from={from} to={to} />}
          {tab === 1 && <Explorateur fleetId={fleetId} from={from} to={to} />}
          {tab === 2 && <Anomalies fleetId={fleetId} from={from} to={to} />}
          {tab === 3 && <Couts fleetId={fleetId} />}
        </Box>
      </Box>
        </FeatureGate>
      </Box>
    </Sidebar>
  );
};

export default Analyses;
